export const DEFAULT_PAN_ARROW_STEP_PX: number = 24;

export type PhotoEditorHotkeysShouldHandle = () => boolean;

export interface BindPhotoEditorHotkeysOptions {
	readonly onZoomIn: () => void;
	readonly onZoomOut: () => void;
	readonly onPanArrow?: (dirX: number, dirY: number) => void;
	readonly isPanArrowActive?: () => boolean;
	readonly shouldHandle?: PhotoEditorHotkeysShouldHandle;
}

function isEditableEventTarget(target: EventTarget | null): boolean {
	if (!(target instanceof Element)) return false;
	return Boolean(target.closest('input, textarea, select, [contenteditable="true"]'));
}

export function bindPhotoEditorHotkeys(
	target: Window | HTMLElement,
	options: BindPhotoEditorHotkeysOptions
): () => void {
	const shouldHandle: PhotoEditorHotkeysShouldHandle = options.shouldHandle ?? ((): boolean => true);

	const onKeyDown: (event: KeyboardEvent) => void = (event: KeyboardEvent): void => {
		if (event.defaultPrevented) return;
		if (!shouldHandle()) return;
		if (isEditableEventTarget(event.target)) return;

		const mod: boolean = event.metaKey || event.ctrlKey;

		if (mod && !event.altKey) {
			if (event.code === 'Equal' || event.code === 'NumpadAdd') {
				event.preventDefault();
				options.onZoomIn();
				return;
			}
			if (event.code === 'Minus' || event.code === 'NumpadSubtract') {
				event.preventDefault();
				options.onZoomOut();
				return;
			}
		}

		if (mod || event.altKey) return;

		if (!options.isPanArrowActive?.()) return;

		const arrowDirs: Partial<Record<string, readonly [number, number]>> = {
			ArrowUp: [0, -1],
			ArrowDown: [0, 1],
			ArrowLeft: [-1, 0],
			ArrowRight: [1, 0]
		};
		const dir: readonly [number, number] | undefined = arrowDirs[event.code];
		if (dir) {
			event.preventDefault();
			options.onPanArrow?.(dir[0], dir[1]);
		}
	};

	target.addEventListener('keydown', onKeyDown);
	return (): void => {
		target.removeEventListener('keydown', onKeyDown);
	};
}
